import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { FieldErrors, UseFormRegister } from 'react-hook-form';

import { validateDuplicateLabName, validateLabelSettings } from './formUtils';

export interface LabFormValues {
  labName: string;
  labelSettings: string;
  isStagingLab?: boolean;
  isTestLab?: boolean;
}

interface LabFormFieldsProps {
  disabled: boolean;
  errors: FieldErrors<LabFormValues>;
  existingLabNames: string[];
  labelSettingsHelperText: string;
  register: UseFormRegister<LabFormValues>;
  labNameInputTestId: string;
  labelSettingsInputTestId: string;
}

export const LabFormFields: React.FC<LabFormFieldsProps> = ({
  disabled,
  errors,
  existingLabNames,
  labelSettingsHelperText,
  register,
  labNameInputTestId,
  labelSettingsInputTestId,
}) => {
  return (
    <>
      <Grid item>
        <TextField
          label="Lab Name"
          required
          fullWidth
          disabled={disabled}
          error={Boolean(errors.labName)}
          helperText={errors.labName?.message}
          inputProps={{ 'data-testid': labNameInputTestId }}
          {...register('labName', {
            required: 'Lab name is required',
            validate: (value) => validateDuplicateLabName(value, existingLabNames),
          })}
        />
      </Grid>
      <Grid item>
        <Accordion
          disableGutters
          elevation={0}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="advanced-settings-content"
            id="advanced-settings-header"
          >
            <Typography variant="body2" color="text.secondary">
              Advanced Settings
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel control={<Checkbox disabled={disabled} {...register('isTestLab')} />} label="Test lab" />
            <FormHelperText sx={{ mb: 2 }}>
              New test copies are marked not for training. Existing slides are unchanged.
            </FormHelperText>
            <TextField
              label="Label Settings (optional JSON object)"
              fullWidth
              multiline
              minRows={3}
              placeholder='{"key": "value"}'
              disabled={disabled}
              error={Boolean(errors.labelSettings)}
              helperText={errors.labelSettings?.message ?? labelSettingsHelperText}
              inputProps={{ 'data-testid': labelSettingsInputTestId }}
              {...register('labelSettings', { validate: validateLabelSettings })}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
    </>
  );
};
